import React from "react";
import { Control } from "react-hook-form";
import {
	FormField,
	FormItem,
	FormControl,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@hipotecar/ui/input";

interface EqualSignInputProps {
	label: string;
	required?: boolean;
	leftValue: number;
	leftSuffix: string;
	onLeftChange: (value: number) => void;
	rightFieldName: string;
	rightSuffix: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	control: Control<any>;
	conversionFactor?: number;
	disabled?: boolean;
}

/**
 * Two linked inputs with conversion via equals sign.
 * Single label on top describes both inputs.
 * Example: "Plazo"  [240 Meses] = [20 Años]
 * Left input is form-controlled, right input is calculated
 */
const EqualSignInput: React.FC<EqualSignInputProps> = ({
	label,
	required = false,
	leftValue,
	leftSuffix,
	onLeftChange,
	rightFieldName,
	rightSuffix,
	control,
	conversionFactor,
	disabled = false,
}) => {
	const handleMonthsInputChange = (
		rawValue: string,
		onChange: (value: number) => void,
	) => {
		// Reject decimal/exponential/not-a-number input for month fields.
		if (!/^\d*$/.test(rawValue)) {
			return;
		}
		const value = rawValue === "" ? 0 : parseInt(rawValue, 10);
		onChange(isNaN(value) ? 0 : value);
		if (conversionFactor && !isNaN(value)) {
			onLeftChange(value / conversionFactor);
		}
	};

	return (
		<div>
			{/* Single label for both inputs */}
			<label className="label-text">
				{label}
				{required && <span className="text-red-500"> *</span>}
			</label>

			{/* Grid with left input, equals sign, right input */}
			<div className="grid grid-cols-[1fr_auto_1fr] gap-3">
				{/* Left Input (Form Controlled) */}
				<FormField
					control={control}
					name={rightFieldName}
					render={({ field }) => (
						<FormItem>
							<FormControl>
								<div className="relative">
									<Input
										type="number"
										value={String(field.value as number)}
										step="1"
										min="1"
										onChange={(e) =>
											handleMonthsInputChange(e.target.value, field.onChange)
										}
										onKeyDown={(e) => {
											if ([".", ",", "e", "E", "+", "-"].includes(e.key)) {
												e.preventDefault();
											}
										}}
										className={rightSuffix ? "pr-12" : ""}
										disabled={disabled}
										onFocus={(e) => e.target.select()}
									/>
									{rightSuffix && (
										<span className="input-addon">{rightSuffix}</span>
									)}
								</div>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{/* Equals Sign */}
				<div className="flex h-11 items-center justify-center text-tertiary">
					=
				</div>

				{/* Right Input (Uncontrolled - Years) */}
				<div className="relative">
					<Input
						type="number"
						step="0.01"
						value={Number(leftValue.toFixed(2))}
						onChange={(e) => {
							const val = parseFloat(e.target.value) || 0;
							// Round to 2 decimal places
							onLeftChange(Math.round(val * 100) / 100);
						}}
						className={leftSuffix ? "pr-12" : ""}
						disabled={disabled}
						onFocus={(e) => e.target.select()}
					/>
					{leftSuffix && <span className="input-addon">{leftSuffix}</span>}
				</div>
			</div>
		</div>
	);
};

export default EqualSignInput;
